import { memo, useMemo } from 'react'
import { useRecentTrades } from 'hooks/useRecentTrades'
import { useMarketTokensWithPrices } from 'hooks/useMarketTokens'
import Change from '../shared/Change'
import { useTokenPairSummary } from 'hooks/useTokenPairSummary'
import SheenLoader from '../loading/SheenLoader'
import { useMintInfo } from 'hooks/useMintInfo'
import { useBirdeyeTokenOverview } from 'hooks/useBirdeyeTokenOverview'
import Head from 'next/head'
import { USDC_MINT } from 'utils/constants'
import TokenLogo from '../shared/TokenLogo'
import { useWalletTokens } from 'hooks/useWalletTokens'
import { formatNumericValue, numberCompacter } from 'utils/numbers'
import store from 'stores/store'
import NumberFlash from '@components/shared/NumberFlash'
import { useTranslation } from 'next-i18next'
import { milliseconds } from 'date-fns'

const SHARED_TEXT_CLASS = 'text-base font-medium tabular-nums text-th-fgd-1'

const MarketHeroStats = memo(function MarketHeroStats() {
  const { t } = useTranslation('token-page')
  const { data: tokenOverview } = useBirdeyeTokenOverview()
  const { liquidity } = tokenOverview ?? {}
  const baseToken = store((s) => s.market.baseToken)
  const quoteToken = store((s) => s.market.quoteToken)
  const { baseMintPrice, quoteMintPrice } = useMarketTokensWithPrices()
  const { sellVolume, buyVolume } = useRecentTrades()
  const { change24hPercentage, tokenStats, isInitialLoading } =
    useTokenPairSummary({ refetchInterval: milliseconds({ seconds: 10 }) })
  const { mintInfoData } = useMintInfo(baseToken.mint, baseToken.token_program)
  const { mintInfo } = mintInfoData ?? {}
  const { walletTokens } = useWalletTokens()

  const walletBalance = useMemo(() => {
    if (!walletTokens?.length) return 0
    return (
      walletTokens.find((token) => token.mint.toString() === baseToken.mint)
        ?.uiAmount || 0
    )
  }, [baseToken.mint, walletTokens])

  const isUsdcQuote = quoteToken.mint === USDC_MINT

  const volume24h = useMemo(() => {
    // Both tokenStats volumes (Birdeye token_overview) and realtime trade
    // volumes (Birdeye trade.price * size) are USD-denominated now.
    const initialUsd =
      (tokenStats?.d1?.volume?.buy?.volume || 0) +
      (tokenStats?.d1?.volume?.sell?.volume || 0)
    return initialUsd + buyVolume + sellVolume
  }, [tokenStats, buyVolume, sellVolume])

  const liq = useMemo(() => {
    if (!liquidity) return 0
    const liquidityChange = buyVolume - sellVolume
    return liquidity + liquidityChange
  }, [buyVolume, liquidity, sellVolume])

  const marketCap = useMemo(() => {
    if (
      !baseMintPrice ||
      !mintInfo?.supply ||
      (!isUsdcQuote && !quoteMintPrice)
    ) {
      return
    }
    const quoteMultiplier = isUsdcQuote ? 1 : quoteMintPrice
    return baseMintPrice * mintInfo.supply * quoteMultiplier
  }, [mintInfo, baseMintPrice, isUsdcQuote, quoteMintPrice])

  const rawPrice = isUsdcQuote ? baseMintPrice : baseMintPrice / quoteMintPrice

  const metaTitle = t('meta.title', { symbol: baseToken.symbol })

  return (
    <>
      <Head>
        <title>
          {baseToken.symbol} | {rawPrice ? formatNumericValue(rawPrice) : ''}{' '}
          {quoteToken.symbol} | Cabana
        </title>
        <meta property="og:title" content={metaTitle} />
        <meta name="twitter:title" content={metaTitle} />
      </Head>
      <div className="hide-scroll grid grid-cols-2 gap-4 overflow-x-auto md:grid-cols-5">
        <div>
          <p className="whitespace-nowrap text-xs">
            {t('stats.price', { symbol: quoteToken.symbol })}
          </p>
          {isInitialLoading ? (
            <>
              <SheenLoader className="my-1">
                <div className="h-[24px] w-20 bg-th-bkg-2" />
              </SheenLoader>
              <SheenLoader>
                <div className="h-[14px] w-10 bg-th-bkg-2" />
              </SheenLoader>
            </>
          ) : (
            <>
              <span className={SHARED_TEXT_CLASS}>
                {rawPrice ? (
                  <NumberFlash number={formatNumericValue(rawPrice)} />
                ) : (
                  '–'
                )}
              </span>
              {change24hPercentage != null &&
              Number.isFinite(change24hPercentage) ? (
                <Change change={change24hPercentage} size="small" suffix="%" />
              ) : (
                <p className="leading-none">–</p>
              )}
            </>
          )}
        </div>
        <div className="flex space-x-4">
          <div>
            <p className="whitespace-nowrap text-xs">
              {tokenStats?.d1?.volume?.buy?.volume == null &&
              tokenStats?.d1?.volume?.sell?.volume == null &&
              !isInitialLoading
                ? t('stats.volume.realtime')
                : t('stats.volume.24h')}
            </p>
            {isInitialLoading ? (
              <SheenLoader className="mt-1">
                <div className="h-[24px] w-20 bg-th-bkg-2" />
              </SheenLoader>
            ) : (
              <span className={SHARED_TEXT_CLASS}>
                {volume24h ? `$${numberCompacter.format(volume24h)}` : '–'}
              </span>
            )}
          </div>
        </div>
        <div className="flex md:space-x-4">
          <div>
            <p className="whitespace-nowrap text-xs">{t('stats.fdv')}</p>
            <span className={SHARED_TEXT_CLASS}>
              {marketCap ? `$${numberCompacter.format(marketCap)}` : '–'}
            </span>
          </div>
        </div>
        <div className="flex space-x-4">
          <div>
            <p className="whitespace-nowrap text-xs">{t('stats.liquidity')}</p>
            <span className={SHARED_TEXT_CLASS}>
              {liq ? `$${numberCompacter.format(liq)}` : '–'}
            </span>
          </div>
        </div>
        <div className="flex space-x-4">
          <div>
            <p className="whitespace-nowrap text-xs">{t('stats.balance')}</p>
            <div className="flex items-center space-x-2">
              <TokenLogo logoUrl={baseToken.image_url} size="small" />
              <span className={SHARED_TEXT_CLASS}>
                {baseToken.decimals
                  ? formatNumericValue(walletBalance, baseToken.decimals)
                  : walletBalance}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
})

export default MarketHeroStats
